import React from 'react';
import {
  Package, FolderOpen, Search, Waves, Shield, Wind,
  CheckCircle, Box, Truck, ArrowRight, Building2, MapPin
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const processSteps = [
  {
    id: 1,
    title: "Pickup",
    description: "Scheduled collection from the campus or facility for hassle-free service",
    icon: "package",
    image: "/images/1.png"
  },
  {
    id: 2,
    title: "Sorting & Coding",
    description: "Garments are sorted and coded for accurate tracking.",
    icon: "folderOpen",
    image: "/images/2.png"
  },
  {
    id: 3,
    title: "Inspection",
    description: "Items are checked for stains, fabric condition, and special care needs",
    icon: "search",
    image: "/images/3.png"
  },
  {
    id: 4,
    title: "Washing",
    description: "Washed using RO-treated water at controlled temperatures, eco-friendly detergents, and commercial-grade machines.",
    icon: "waves",
    image: "/images/4.png"
  },
  {
    id: 5,
    title: "Fabric Care",
    description: "Extra care is taken to maintain color, softness, and durability.",
    icon: "shield",
    image: "/images/5.jpg"
  },
  {
    id: 6,
    title: "Drying & Finishing",
    description: "Clothes are dried, pressed, or folded as required.",
    icon: "wind",
    image: "/images/6.png"
  },
  {
    id: 7,
    title: "Quality Check",
    description: "Each batch is inspected before dispatch.",
    icon: "checkCircle",
    image: "/images/7.jpg"
  },
  {
    id: 8,
    title: "Delivery",
    description: "Securely packed and delivered on time.",
    icon: "box",
    image: "/images/8.jpg"
  }
];

const ProcessIcons = {
  package: Package,
  folderOpen: FolderOpen,
  search: Search,
  waves: Waves,
  shield: Shield,
  wind: Wind,
  checkCircle: CheckCircle,
  box: Box
};

const Process = () => {

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-sky-700 text-white py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4">Our Laundry Process</h1>
          <p className="text-base md:text-2xl text-sky-100 max-w-3xl mx-auto">
            A systematic, step-by-step approach ensuring quality at every stage
          </p>
        </div>
      </section>

      {/* Intro + Steps — single unified section, no gap */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-0 text-center">
          <p className="text-base text-gray-600 leading-relaxed text-justify">
            Our laundry solution delivers clean, fresh, and well-maintained garments with complete reliability. Every step is carefully managed to ensure high hygiene standards, advanced fabric care, and on-time delivery. Using eco-friendly detergents, modern washing techniques, and strict quality checks, we protect fabric life while providing safe, hygienic laundry services. Our efficient pickup and delivery system ensures a hassle-free experience, saving time while maintaining cleanliness and comfort.
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-4 relative pt-6 pb-16">

          {/* MOBILE: image-first vertical card list */}
          <div className="md:hidden flex flex-col gap-5">
            {processSteps.map((step) => {
              const Icon = ProcessIcons[step.icon];
              return (
                <div key={step.id} className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
                  {/* Large image on top */}
                  <div className="w-full h-48 bg-white">
                    <img src={step.image} alt={step.title} className="w-full h-full object-contain" />
                  </div>
                  {/* Content below */}
                  <div className="flex items-start gap-3 p-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-sky-600 flex items-center justify-center shadow">
                      <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-sky-600 font-medium mb-0.5">Step {String(step.id).padStart(2, '0')}</div>
                      <h3 className="text-sm font-bold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP: zigzag timeline (unchanged) */}
          {/* Starting dot */}
          <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 top-[3.5rem] w-4 h-4 z-20">
            <div className="w-4 h-4 rounded-full bg-sky-600 shadow-md border-2 border-white"></div>
          </div>

          {/* Vertical line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 top-[4.5rem] bottom-12 w-0.5 bg-gray-300" />

          {processSteps.map((step, index) => {
            const isLeft = index % 2 === 0;
            const Icon = ProcessIcons[step.icon];

            return (
              <div
                key={step.id}
                className="relative hidden md:flex items-center justify-center py-10"
              >
                <div className="w-full flex items-center justify-center relative z-10 gap-4">

                  <div className="flex-1 flex justify-end">
                    {isLeft ? (
                      <div className="w-52 h-40 bg-gradient-to-br from-white to-blue-50 rounded-lg border border-blue-100 overflow-hidden shadow-sm flex items-center justify-center">
                        <img src={step.image} alt={step.title} className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                        <div style={{ display: 'none' }} className="w-full h-full bg-gray-200 flex items-center justify-center text-center text-sm text-gray-600 p-4">{step.title}</div>
                      </div>
                    ) : (
                      <div className="text-right max-w-[200px] w-full">
                        <div className="text-xs text-sky-600 font-medium mb-1">Step {String(step.id).padStart(2, '0')}</div>
                        <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0 w-16 h-16 relative flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-sky-600 flex items-center justify-center z-10 shadow-md border-4 border-white">
                      <Icon className="w-8 h-8 text-white" strokeWidth={2} />
                    </div>
                  </div>

                  <div className="flex-1 flex justify-start">
                    {isLeft ? (
                      <div className="text-left max-w-[200px] w-full">
                        <div className="text-xs text-sky-600 font-medium mb-1">Step {String(step.id).padStart(2, '0')}</div>
                        <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    ) : (
                      <div className="w-52 h-40 bg-gradient-to-br from-white to-blue-50 rounded-lg border border-blue-100 overflow-hidden shadow-sm flex items-center justify-center">
                        <img src={step.image} alt={step.title} className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                        <div style={{ display: 'none' }} className="w-full h-full bg-gray-200 flex items-center justify-center text-center text-sm text-gray-600 p-4">{step.title}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Ending dot */}
          <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 bottom-12 w-6 h-6 z-20">
            <div className="w-6 h-6 rounded-full bg-sky-600 shadow-md border-4 border-white"></div>
          </div>
        </div>
      </section>

      {/* Process Highlights */}
      <section className="py-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-2">
              What Makes Our Solution Special
            </h2>
          </div>

          {/* Row 1: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Card className="border-0 shadow-md">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 text-sky-600 rounded-full mb-3"><Shield className="h-6 w-6" /></div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Fabric Safety</h3>
                <p className="text-sm text-gray-600">Eco-friendly detergents and gentle handling to preserve fabric quality and color</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 text-sky-600 rounded-full mb-3"><CheckCircle className="h-6 w-6" /></div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Quality Control</h3>
                <p className="text-sm text-gray-600">Manual checks at multiple stages to ensure every garment meets our standards</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 text-sky-600 rounded-full mb-3"><Truck className="h-6 w-6" /></div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Timely Delivery</h3>
                <p className="text-sm text-gray-600">Scheduled pickup and delivery to ensure your laundry is always on time</p>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: 2 cards — centred */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <Card className="border-0 shadow-md">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 text-sky-600 rounded-full mb-3"><Package className="h-6 w-6" /></div>
                <h3 className="text-base font-bold text-gray-900 mb-2">ERP Tracking</h3>
                <p className="text-sm text-gray-600">End-to-end tracking lets you monitor your laundry's journey from pickup to processing and delivery in real-time</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 text-sky-600 rounded-full mb-3"><CheckCircle className="h-6 w-6" /></div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Student-Friendly Billing</h3>
                <p className="text-sm text-gray-600">Itemized digital billing for every order, ensuring complete transparency and budget-friendly tracking for students</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Operational Setup */}
      <section className="py-10 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Our Operational Setup
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer flexible operational models to suit your institution's needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-sky-200 shadow-lg bg-sky-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center mr-4">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">On-Campus</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  We set up laundry units inside your campus so we can process clothes faster,
                  monitor quality easily, and manage daily laundry without delays.
                </p>
              </CardContent>
            </Card>

            <Card className="border-sky-200 bg-sky-50 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center mr-4">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Off-Campus</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  We run processing units near your campus with daily pickup and delivery,
                  and we can set up units anywhere in India based on your needs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 lg:py-24 bg-gradient-to-r from-blue-900 to-sky-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">
            Experience Our Solution-Driven Approach
          </h2>
          <p className="text-base md:text-xl mb-6 md:mb-8 text-sky-100">
            Let us handle your laundry with care, consistency, and professionalism
          </p>
          <a href="/contact">
            <button className="bg-white text-sky-600 hover:bg-sky-50 font-semibold px-8 py-4 rounded-lg text-lg inline-flex items-center transition-colors shadow-lg">
              Get Started Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Process;